import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  CalendarClock, Phone, Mail, LogOut, RefreshCw, CheckCircle2, Archive, Inbox,
  ChevronLeft, ChevronRight, X, MapPin, User, Baby, BarChart3, List as ListIcon, CalendarDays, Save,
  Search as SearchIcon, Bell, History, Send, PlusCircle,
  Sun, Moon, Sunrise, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "tlt_admin_token";
const VIEW_KEY = "tlt_admin_view";

const SOURCE_OPTIONS = ["Website", "Google", "Referral", "Call-in", "Other"];
const DISPOSITION_OPTIONS = ["New Lead", "Tour Scheduled", "Toured", "Enrolled", "Not Interested", "No Show"];
const SOURCE_COLORS = { Website: "#2E7CD6", Google: "#D7352B", Referral: "#3D9B4F", "Call-in": "#E8622A", Other: "#B98A00", Unknown: "#6B7280" };

const STATUS_STYLES = {
  new: { bg: "#FDE7E7", fg: "#D7352B", label: "New" },
  contacted: { bg: "#E6F4EA", fg: "#3D9B4F", label: "Contacted" },
  archived: { bg: "#ECEAE4", fg: "#6B7280", label: "Archived" },
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const inputCls = "mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 text-forest text-sm focus:outline-none focus:ring-2 focus:ring-[#D7352B]/40 bg-white";

function fmtTime(timeSlot) {
  if (!timeSlot) return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(timeSlot);
  if (!m) return timeSlot;
  const hh = parseInt(m[1], 10);
  return `${hh % 12 || 12}:${m[2]} ${hh < 12 ? "AM" : "PM"}`;
}
function fmtDate(dateStr, timeSlot) {
  if (!dateStr) return "Flexible";
  let out = dateStr;
  try {
    out = new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  } catch (e) {} // eslint-disable-line no-empty
  return timeSlot ? `${out}, ${fmtTime(timeSlot)}` : out;
}
function weekStart(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function tourDateTime(tour) {
  if (!tour.preferred_date) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(tour.time_slot || "");
  const dt = new Date(tour.preferred_date + "T00:00:00");
  if (m) dt.setHours(parseInt(m[1], 10), parseInt(m[2], 10));
  return dt;
}
// Needs follow-up: had a scheduled tour that finished >= 24h ago, not yet followed up, not archived.
function needsFollowUp(tour) {
  if (tour.followed_up || tour.status === "archived") return false;
  const dt = tourDateTime(tour);
  if (!dt) return false;
  return Date.now() - dt.getTime() >= 24 * 60 * 60 * 1000;
}
function fmtNoteTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) { return iso; }
}
function weatherInfo(code) {
  if (code === 0) return { label: "Sunny", Icon: Sun };
  if (code >= 1 && code <= 2) return { label: "Partly cloudy", Icon: CloudSun };
  if (code === 3) return { label: "Cloudy", Icon: Cloud };
  if (code === 45 || code === 48) return { label: "Foggy", Icon: CloudFog };
  if (code >= 51 && code <= 57) return { label: "Drizzle", Icon: CloudDrizzle };
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return { label: "Rainy", Icon: CloudRain };
  if (code >= 71 && code <= 77) return { label: "Snowy", Icon: CloudSnow };
  if (code >= 95) return { label: "Storms", Icon: CloudLightning };
  return { label: "Pleasant", Icon: CloudSun };
}

/* ---------------- Cheery greeting banner ---------------- */
const CHEERS = ["Let's make it a wonderful day!", "The little ones are lucky to have you!", "Here's to happy little learners!", "Every day is a new adventure!", "Growing bright minds, one smile at a time!"];

function GreetingBanner() {
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const cheer = useMemo(() => CHEERS[Math.floor(Math.random() * CHEERS.length)], []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let ignore = false;
    fetch(`${API}/weather`)
      .then((r) => r.json())
      .then((d) => { if (!ignore && d && d.temp != null) setWeather({ temp: d.temp, code: d.code }); })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  const hr = now.getHours();
  const greeting = hr < 12 ? "Good morning" : hr < 17 ? "Good afternoon" : "Good evening";
  const GreetIcon = hr < 12 ? Sunrise : hr < 17 ? Sun : Moon;
  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const w = weather ? weatherInfo(weather.code) : null;
  const WIcon = w ? w.Icon : CloudSun;

  const dots = [
    { c: "#FFC400", top: "18%", left: "42%" }, { c: "#2E7CD6", top: "62%", left: "30%" },
    { c: "#43A047", top: "30%", left: "88%" }, { c: "#ffffff", top: "72%", left: "76%" },
  ];

  return (
    <div
      data-testid="admin-greeting-banner"
      className="relative overflow-hidden rounded-2xl p-4 sm:p-5 mb-4 shadow-lg"
      style={{ background: "linear-gradient(120deg, #D7352B 0%, #E8622A 42%, #FFA41B 100%)" }}
    >
      <div className="absolute inset-0 pointer-events-none">
        {dots.map((d, i) => (
          <span key={i} className="absolute w-3.5 h-3.5 rounded-full animate-float-slow opacity-80" style={{ background: d.c, top: d.top, left: d.left, animationDelay: `${i * 0.7}s` }} />
        ))}
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 left-1/3 w-56 h-56 rounded-full bg-white/5" />
      </div>

      <div className="relative flex items-center justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
              <GreetIcon className="w-6 h-6 text-white" />
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white drop-shadow" style={{ fontFamily: "Fredoka, sans-serif" }} data-testid="greeting-text">
              {greeting}!
            </h2>
          </div>
          <p className="mt-2 text-white/95 font-semibold flex items-center gap-2 text-sm">
            <Sparkles className="w-4 h-4 text-amber flex-shrink-0" /> {cheer}
          </p>
        </div>

        <div className="flex items-stretch gap-3">
          <div className="rounded-xl bg-white/15 backdrop-blur-sm px-4 py-2 text-white text-center" data-testid="greeting-datetime">
            <p className="text-2xl font-bold leading-none" style={{ fontFamily: "Fredoka, sans-serif" }}>{timeStr}</p>
            <p className="text-[11px] font-semibold text-white/85 mt-1">{dateStr}</p>
          </div>
          <div className="rounded-xl bg-white/15 backdrop-blur-sm px-4 py-2 text-white text-center flex flex-col items-center justify-center min-w-[84px]" data-testid="greeting-weather">
            <WIcon className="w-6 h-6 text-amber" />
            {weather ? (
              <>
                <p className="text-xl font-bold leading-none mt-1" style={{ fontFamily: "Fredoka, sans-serif" }}>{weather.temp}°</p>
                <p className="text-[11px] font-semibold text-white/85 mt-0.5">{w.label}</p>
              </>
            ) : (
              <p className="text-[11px] font-semibold text-white/85 mt-1">Palm Bay</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Login ---------------- */
function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onSuccess(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream grain flex items-center justify-center px-4" data-testid="admin-login-screen">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-black/5">
        <div className="w-14 h-14 rounded-2xl bg-[#FDE7E7] flex items-center justify-center mb-5">
          <Inbox className="w-7 h-7" style={{ color: "#D7352B" }} />
        </div>
        <h1 className="text-3xl font-bold text-forest" style={{ fontFamily: "Fredoka, sans-serif" }}>Staff Login</h1>
        <p className="text-sm text-forest/60 mt-2 mb-6">Sign in to view tour requests.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input" className={inputCls} placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input" className={inputCls} placeholder="••••••••" />
          </div>
          {error && <p className="text-sm font-semibold" style={{ color: "#D7352B" }} data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full rounded-full bg-[#D7352B] text-white font-bold py-3.5 hover:brightness-110 transition disabled:opacity-60">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Detail editor ---------------- */
function TourDetail({ tour, token, onClose, onSaved }) {
  const [f, setF] = useState({
    parent_name: tour.parent_name || "",
    phone: tour.phone || "",
    email: tour.email || "",
    address: tour.address || "",
    child_name: tour.child_name || "",
    child_age: tour.child_age || "",
    source: tour.source || "",
    disposition: tour.disposition || "",
    admin_notes: tour.admin_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setSaved(false); setErr(""); };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await axios.patch(`${API}/admin/tours/${tour.id}`, f, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data.request);
      setSaved(true);
    } catch (e) {
      setErr("Could not save — please try again.");
    } finally { setSaving(false); }
  };

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setAddingNote(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/tours/${tour.id}/notes`, { text }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data.request);
      setNewNote("");
    } catch (e) { setErr("Could not add note."); } finally { setAddingNote(false); }
  };

  const toggleFollowUp = async () => {
    setErr("");
    try {
      const { data } = await axios.patch(`${API}/admin/tours/${tour.id}`, { followed_up: !tour.followed_up }, { headers: { Authorization: `Bearer ${token}` } });
      onSaved(data.request);
    } catch (e) { setErr("Could not update follow-up."); }
  };

  const history = [
    ...(tour.admin_notes ? [{ id: "legacy", text: tour.admin_notes, created_at: tour.created_at, status: "", disposition: "" }] : []),
    ...((tour.notes || []).map((n) => n)),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-detail-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-cream h-full flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-forest text-cream px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold" style={{ fontFamily: "Fredoka, sans-serif" }}>Tour Details</h2>
          <button onClick={onClose} data-testid="admin-detail-close" className="p-1.5 rounded-full hover:bg-white/15"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 pb-12" data-testid="admin-detail-scroll">
          <div className="bg-white rounded-2xl p-3 border border-black/5">
            <p className="text-xs font-bold uppercase tracking-wider text-forest/40 mb-1.5">What they booked</p>
            <p className="flex items-center gap-2 text-sm font-semibold text-forest">
              <CalendarClock className="w-4 h-4 text-tangerine" /> {fmtDate(tour.preferred_date, tour.time_slot)}
            </p>
            {tour.message && <p className="mt-2 text-sm text-forest/70 italic">"{tour.message}"</p>}
            <p className="mt-2 text-xs text-forest/40">Received {tour.created_at ? new Date(tour.created_at).toLocaleString("en-US") : "—"}</p>
          </div>

          <div className="space-y-2.5">
            <Field icon={User} label="Parent name" value={f.parent_name} onChange={set("parent_name")} testid="detail-parent-name" />
            <Field icon={Phone} label="Phone" value={f.phone} onChange={set("phone")} testid="detail-phone" />
            <Field icon={Mail} label="Email" value={f.email} onChange={set("email")} testid="detail-email" />
            <Field icon={MapPin} label="Address" value={f.address} onChange={set("address")} placeholder="Street, city…" testid="detail-address" />
            <Field icon={Baby} label="Child's name" value={f.child_name} onChange={set("child_name")} testid="detail-child-name" />
            <Field icon={Baby} label="Child's age" value={f.child_age} onChange={set("child_age")} testid="detail-child-age" />

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-forest/50">How did you hear about us?</label>
              <select value={f.source} onChange={set("source")} className={inputCls} data-testid="detail-source">
                <option value="">— Select —</option>
                {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Disposition</label>
              <select value={f.disposition} onChange={set("disposition")} className={inputCls} data-testid="detail-disposition">
                <option value="">— Select —</option>
                {DISPOSITION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {err && <p className="text-sm font-semibold text-center" style={{ color: "#D7352B" }} data-testid="admin-detail-error">{err}</p>}
          <button onClick={save} disabled={saving} data-testid="admin-detail-save" className="w-full rounded-full bg-[#D7352B] text-white font-bold py-3.5 flex items-center justify-center gap-2 hover:brightness-110 transition disabled:opacity-60">
            {saved ? <><CheckCircle2 className="w-5 h-5" /> Saved</> : <><Save className="w-5 h-5" /> {saving ? "Saving…" : "Save details"}</>}
          </button>

          {tour.followed_up ? (
            <button onClick={toggleFollowUp} data-testid="detail-toggle-followup" className="w-full rounded-full bg-[#E6F4EA] text-[#3D9B4F] font-bold py-3 flex items-center justify-center gap-2 hover:brightness-95 transition">
              <CheckCircle2 className="w-5 h-5" /> Followed up — tap to undo
            </button>
          ) : needsFollowUp(tour) ? (
            <button onClick={toggleFollowUp} data-testid="detail-toggle-followup" className="w-full rounded-full bg-[#D7352B] text-white font-bold py-3 flex items-center justify-center gap-2 hover:brightness-110 transition animate-pulse">
              <Bell className="w-5 h-5" /> Follow-up due — mark done
            </button>
          ) : null}

          <div className="bg-white rounded-2xl p-4 border border-black/5" data-testid="detail-notes-history">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-forest/40 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Notes &amp; history</p>
              {history.length > 0 && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-cream text-forest/60" data-testid="detail-notes-count">{history.length} {history.length === 1 ? "note" : "notes"}</span>}
            </div>
            <textarea rows={2} value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note (call summary, tour outcome…)" className={`${inputCls} mt-0 resize-none`} data-testid="detail-note-input" />
            <button onClick={addNote} disabled={addingNote || !newNote.trim()} data-testid="detail-add-note" className="w-full rounded-full bg-forest text-cream text-sm font-bold py-2.5 flex items-center justify-center gap-2 hover:brightness-110 transition disabled:opacity-50 mt-2 mb-4">
              <Send className="w-4 h-4" /> {addingNote ? "Adding…" : "Add note"}
            </button>
            {history.length === 0 ? (
              <p className="text-sm text-forest/40 italic">No notes yet. Add your first note above.</p>
            ) : (
              <>
                <p className="text-[10px] font-bold uppercase tracking-widest text-forest/30 mb-2">History · newest first</p>
                <ul className="space-y-2">
                  {history.map((n) => (
                    <li key={n.id} className="rounded-xl bg-cream p-3" data-testid="detail-note-item">
                      <p className="text-[11px] font-bold text-forest/60 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3 text-tangerine" />{fmtNoteTime(n.created_at)}</span>
                        {n.status && <span className="px-1.5 py-0.5 rounded-full" style={{ background: STATUS_STYLES[n.status]?.bg, color: STATUS_STYLES[n.status]?.fg }}>{STATUS_STYLES[n.status]?.label || n.status}</span>}
                        {n.disposition && <span className="px-1.5 py-0.5 rounded-full bg-[#E3EFFC] text-[#1C3D6E]">{n.disposition}</span>}
                      </p>
                      <p className="text-sm text-forest mt-1 whitespace-pre-wrap">{n.text}</p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-forest/50 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</label>
      <input value={value} onChange={onChange} placeholder={placeholder} className={inputCls} data-testid={testid} />
    </div>
  );
}

/* ---------------- Calendar view ---------------- */
function CalendarView({ tours, onSelect, onAdd }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const byDate = useMemo(() => {
    const map = {};
    tours.forEach((t) => {
      if (!t.preferred_date) return;
      (map[t.preferred_date] = map[t.preferred_date] || []).push(t);
    });
    return map;
  }, [tours]);

  const flexible = tours.filter((t) => !t.preferred_date && t.status !== "archived");
  const first = new Date(year, month, 1);
  const startBlank = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startBlank; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayStr = new Date().toISOString().split("T")[0];
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div data-testid="admin-calendar-view" className="flex flex-col" style={{ height: "calc(100vh - 340px)", minHeight: "460px" }}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <h3 className="text-xl font-bold text-forest" style={{ fontFamily: "Fredoka, sans-serif" }}>{MONTHS[month]} {year}</h3>
        <div className="flex gap-2">
          <button onClick={onAdd} data-testid="admin-add-appointment-calendar" className="flex items-center gap-2 rounded-full px-4 bg-[#D7352B] text-white text-sm font-bold hover:brightness-110 transition"><PlusCircle className="w-4 h-4" /> Add</button>
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev" className="p-2 rounded-full bg-white border border-black/10 hover:border-forest/40"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }} data-testid="cal-today" className="px-3 rounded-full bg-white border border-black/10 hover:border-forest/40 text-sm font-semibold">Today</button>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next" className="p-2 rounded-full bg-white border border-black/10 hover:border-forest/40"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 mb-1.5 flex-shrink-0">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="text-center text-xs font-bold uppercase tracking-wider text-forest/40 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2 flex-1 min-h-0" style={{ gridAutoRows: "minmax(0, 1fr)" }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="rounded-xl bg-transparent" />;
          const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`;
          const dayTours = (byDate[dateStr] || []).filter((t) => t.status !== "archived");
          const isToday = dateStr === todayStr;
          const isWeekend = i % 7 >= 5;
          return (
            <div key={dateStr} data-testid={`cal-day-${dateStr}`} className={`h-full overflow-hidden flex flex-col rounded-xl p-2 border ${isWeekend ? "bg-[#F4F2EC] border-transparent" : "bg-white border-black/5"}`}>
              <div className={`text-sm font-bold mb-1.5 flex-shrink-0 ${isToday ? "text-white bg-[#D7352B] w-6 h-6 rounded-full flex items-center justify-center" : "text-forest/50"}`}>{d}</div>
              <div className="space-y-1 overflow-hidden">
                {dayTours.slice(0, 4).map((t) => (
                  <button key={t.id} onClick={() => onSelect(t)} data-testid={`cal-event-${t.id}`}
                    className="w-full text-left rounded-md px-2 py-1 text-[11px] font-semibold leading-tight bg-[#E3EFFC] text-[#1C3D6E] hover:bg-[#2E7CD6] hover:text-white transition truncate">
                    {t.time_slot ? fmtTime(t.time_slot) + " " : ""}{t.parent_name}
                  </button>
                ))}
                {dayTours.length > 4 && <p className="text-[10px] text-forest/40 pl-1">+{dayTours.length - 4} more</p>}
              </div>
            </div>
          );
        })}
      </div>

      {flexible.length > 0 && (
        <div className="mt-3 flex-shrink-0">
          <p className="text-xs font-bold uppercase tracking-wider text-forest/40 mb-1.5">Flexible / no date chosen ({flexible.length})</p>
          <div className="flex flex-wrap gap-2 max-h-16 overflow-y-auto">
            {flexible.map((t) => (
              <button key={t.id} onClick={() => onSelect(t)} data-testid={`cal-flexible-${t.id}`} className="rounded-full px-3 py-1.5 text-sm font-semibold bg-[#FFF2C4] text-[#B98A00] hover:brightness-95">{t.parent_name}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Marketing view ---------------- */
function MarketingView({ tours }) {
  const active = tours.filter((t) => t.status !== "archived");
  const months = useMemo(() => {
    const set = new Set();
    active.forEach((t) => { if (t.created_at) { const d = new Date(t.created_at); set.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); } });
    return Array.from(set).sort().reverse();
  }, [active]);
  const [selMonth, setSelMonth] = useState("all");

  const filtered = selMonth === "all" ? active : active.filter((t) => {
    if (!t.created_at) return false;
    const d = new Date(t.created_at);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` === selMonth;
  });

  const sourceCounts = {};
  filtered.forEach((t) => { const s = t.source || "Unknown"; sourceCounts[s] = (sourceCounts[s] || 0) + 1; });
  const sourceEntries = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]);
  const maxSource = Math.max(1, ...sourceEntries.map((e) => e[1]));

  // weekly counts (last 8 weeks by created_at)
  const weekMap = {};
  active.forEach((t) => { if (!t.created_at) return; const ws = weekStart(new Date(t.created_at)); weekMap[ws.toISOString().split("T")[0]] = (weekMap[ws.toISOString().split("T")[0]] || 0) + 1; });
  const weeks = [];
  const curWeek = weekStart(new Date());
  for (let i = 7; i >= 0; i--) {
    const w = new Date(curWeek); w.setDate(w.getDate() - i * 7);
    const key = w.toISOString().split("T")[0];
    weeks.push({ key, label: `${MONTHS[w.getMonth()]} ${w.getDate()}`, count: weekMap[key] || 0 });
  }
  const maxWeek = Math.max(1, ...weeks.map((w) => w.count));

  return (
    <div data-testid="admin-marketing-view" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-xl font-bold text-forest" style={{ fontFamily: "Fredoka, sans-serif" }}>Where bookings come from</h3>
        <select value={selMonth} onChange={(e) => setSelMonth(e.target.value)} data-testid="marketing-month-select" className="rounded-xl border border-black/10 px-4 py-2 text-sm font-semibold text-forest bg-white">
          <option value="all">All time</option>
          {months.map((m) => { const [y, mo] = m.split("-"); return <option key={m} value={m}>{MONTHS[parseInt(mo, 10) - 1]} {y}</option>; })}
        </select>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="marketing-source-cards">
        {SOURCE_OPTIONS.map((s) => (
          <div key={s} className="flex-1 min-w-[130px] bg-white rounded-2xl p-4 border border-black/5">
            <p className="text-3xl font-bold" style={{ color: SOURCE_COLORS[s], fontFamily: "Fredoka, sans-serif" }}>{sourceCounts[s] || 0}</p>
            <p className="text-sm font-semibold text-forest/60">{s}</p>
          </div>
        ))}
        {(sourceCounts.Unknown || 0) > 0 && (
          <div className="flex-1 min-w-[130px] bg-white rounded-2xl p-4 border border-dashed border-black/15">
            <p className="text-3xl font-bold" style={{ color: SOURCE_COLORS.Unknown, fontFamily: "Fredoka, sans-serif" }}>{sourceCounts.Unknown}</p>
            <p className="text-sm font-semibold text-forest/60">Not set yet</p>
          </div>
        )}
        <div className="flex-1 min-w-[130px] bg-forest rounded-2xl p-4">
          <p className="text-3xl font-bold text-amber" style={{ fontFamily: "Fredoka, sans-serif" }}>{filtered.length}</p>
          <p className="text-sm font-semibold text-cream/70">Total bookings</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 border border-black/5">
        <p className="text-xs font-bold uppercase tracking-wider text-forest/40 mb-4">Source breakdown {selMonth === "all" ? "(all time)" : ""}</p>
        {sourceEntries.length === 0 ? <p className="text-sm text-forest/50">No bookings in this period.</p> : (
          <div className="space-y-3">
            {sourceEntries.map(([s, c]) => (
              <div key={s} className="flex items-center gap-3" data-testid={`marketing-bar-${s}`}>
                <span className="w-20 text-sm font-semibold text-forest/70">{s === "Unknown" ? "Not set" : s}</span>
                <div className="flex-1 bg-cream rounded-full h-6 overflow-hidden">
                  <div className="h-full rounded-full flex items-center justify-end px-2 text-xs font-bold text-white" style={{ width: `${(c / maxSource) * 100}%`, background: SOURCE_COLORS[s] || SOURCE_COLORS.Unknown, minWidth: "1.75rem" }}>{c}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {(sourceCounts.Unknown || 0) > 0 && (
          <p className="mt-4 text-xs text-forest/50" data-testid="marketing-source-hint">Tip: open a booking and set "How did you hear about us?" so it counts here instead of "Not set".</p>
        )}
      </div>

      <div className="bg-white rounded-2xl p-5 border border-black/5">
        <p className="text-xs font-bold uppercase tracking-wider text-forest/40 mb-4">Bookings per week (last 8 weeks)</p>
        <div className="flex items-end gap-2 h-48" data-testid="marketing-weekly">
          {weeks.map((w) => (
            <div key={w.key} className="flex-1 flex flex-col items-center h-full">
              <span className="text-xs font-bold text-forest/70 mb-1">{w.count}</span>
              <div className="flex-1 w-full flex items-end">
                <div className="w-full rounded-t-md bg-[#43A047]" style={{ height: `${maxWeek ? (w.count / maxWeek) * 100 : 0}%`, minHeight: w.count ? "6px" : "2px" }} />
              </div>
              <span className="text-[10px] text-forest/40 text-center leading-tight mt-1">{w.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- List view ---------------- */
function ListView({ tours, filter, setFilter, counts, onSelect, onStatus, onAdd }) {
  const filtered = tours.filter((t) => (filter === "all" ? true : (t.status || "new") === filter));
  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="flex gap-2 flex-wrap" data-testid="admin-filter-tabs">
          {["all", "new", "contacted", "archived"].map((key) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={`admin-filter-${key}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold capitalize transition border ${filter === key ? "bg-forest text-cream border-forest" : "bg-white text-forest/70 border-black/10 hover:border-forest/40"}`}>
              {key} <span className="opacity-60">({counts[key]})</span>
            </button>
          ))}
        </div>
        <button onClick={onAdd} data-testid="admin-add-appointment-list" className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold bg-[#D7352B] text-white hover:brightness-110 transition shadow-sm">
          <PlusCircle className="w-4 h-4" /> Add appointment
        </button>
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-black/5" data-testid="admin-empty">
          <Inbox className="w-12 h-12 mx-auto text-forest/20 mb-3" />
          <p className="text-forest/60 font-semibold">No tour requests here yet.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="admin-tours-list">
          {filtered.map((t) => {
            const status = t.status || "new";
            const style = STATUS_STYLES[status] || STATUS_STYLES.new;
            return (
              <div key={t.id} data-testid={`admin-tour-card-${t.id}`} className="bg-white rounded-3xl p-5 sm:p-6 border border-black/5 shadow-sm">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button onClick={() => onSelect(t)} data-testid={`admin-tour-name-${t.id}`} className="text-xl font-bold text-forest hover:text-[#D7352B] transition underline-offset-4 hover:underline text-left" style={{ fontFamily: "Fredoka, sans-serif" }}>
                        {t.parent_name}
                      </button>
                      <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: style.bg, color: style.fg }} data-testid={`admin-tour-status-${t.id}`}>{style.label}</span>
                      {t.disposition && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#E3EFFC] text-[#1C3D6E]">{t.disposition}</span>}
                      {t.source && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-cream text-forest/60">via {t.source}</span>}
                    </div>
                    <p className="text-sm text-forest/50 mt-1">{t.child_name ? `${t.child_name} · ` : ""}Age {t.child_age}</p>
                  </div>
                  <div className="flex items-center gap-2 text-sm font-semibold text-forest/70 bg-cream rounded-xl px-3 py-2">
                    <CalendarClock className="w-4 h-4 text-tangerine" />{fmtDate(t.preferred_date, t.time_slot)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm">
                  <a href={`tel:${t.phone}`} className="flex items-center gap-2 text-blue-600 font-semibold hover:underline"><Phone className="w-4 h-4" /> {t.phone}</a>
                  <a href={`mailto:${t.email}`} className="flex items-center gap-2 text-blue-600 font-semibold hover:underline"><Mail className="w-4 h-4" /> {t.email}</a>
                </div>
                {(t.notes || []).length > 0 && (
                  <div className="mt-3 bg-cream rounded-xl px-4 py-2.5" data-testid={`admin-tour-lastnote-${t.id}`}>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-forest/40 flex items-center gap-1.5">
                      <History className="w-3.5 h-3.5" /> Latest note · {fmtNoteTime(t.notes[t.notes.length - 1].created_at)}
                      {(t.notes.length > 1) && <span className="text-forest/40">· {t.notes.length} total</span>}
                    </p>
                    <p className="text-sm text-forest/80 mt-0.5">{t.notes[t.notes.length - 1].text}</p>
                  </div>
                )}
                {t.message && <p className="mt-3 text-sm text-forest/70 bg-cream rounded-xl px-4 py-3 italic">"{t.message}"</p>}
                <div className="flex gap-2 mt-4 flex-wrap">
                  <button onClick={() => onSelect(t)} data-testid={`admin-open-detail-${t.id}`} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-forest text-cream hover:brightness-110 transition">Open details</button>
                  {status !== "contacted" && <button onClick={() => onStatus(t.id, "contacted")} data-testid={`admin-mark-contacted-${t.id}`} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#E6F4EA] text-[#3D9B4F] hover:brightness-95 transition"><CheckCircle2 className="w-4 h-4" /> Contacted</button>}
                  {status !== "archived" && <button onClick={() => onStatus(t.id, "archived")} data-testid={`admin-mark-archived-${t.id}`} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#ECEAE4] text-gray-600 hover:brightness-95 transition"><Archive className="w-4 h-4" /> Archive</button>}
                  {status !== "new" && <button onClick={() => onStatus(t.id, "new")} data-testid={`admin-mark-new-${t.id}`} className="rounded-full px-4 py-2 text-sm font-semibold bg-[#FDE7E7] text-[#D7352B] hover:brightness-95 transition">Reopen</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Search view ---------------- */
function SearchView({ tours, onSelect }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const qDigits = query.replace(/\D/g, "");
  const results = query
    ? tours.filter((t) =>
        [t.parent_name, t.email, t.phone, t.child_name].some((v) => (v || "").toLowerCase().includes(query)) ||
        (qDigits && (t.phone || "").replace(/\D/g, "").includes(qDigits))
      )
    : [];

  return (
    <div data-testid="admin-search-view">
      <div className="relative max-w-xl mb-6">
        <SearchIcon className="w-5 h-5 text-forest/40 absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          placeholder="Search by name, email, phone or child's name…"
          data-testid="admin-search-input"
          className="w-full rounded-full border border-black/10 pl-12 pr-4 py-3.5 text-forest focus:outline-none focus:ring-2 focus:ring-[#D7352B]/40 bg-white"
        />
      </div>

      {!query ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-black/5">
          <SearchIcon className="w-12 h-12 mx-auto text-forest/20 mb-3" />
          <p className="text-forest/60 font-semibold">Start typing to find any family that booked a tour.</p>
        </div>
      ) : results.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-black/5" data-testid="admin-search-empty">
          <p className="text-forest/60 font-semibold">No matches for "{q}".</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="admin-search-results">
          <p className="text-sm text-forest/50 font-semibold">{results.length} match{results.length > 1 ? "es" : ""}</p>
          {results.map((t) => {
            const style = STATUS_STYLES[t.status || "new"] || STATUS_STYLES.new;
            const lastNote = (t.notes || [])[(t.notes || []).length - 1];
            return (
              <button key={t.id} onClick={() => onSelect(t)} data-testid={`admin-search-result-${t.id}`}
                className="text-left bg-white rounded-2xl p-4 border border-black/5 hover:border-forest/40 transition">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-lg font-bold text-forest" style={{ fontFamily: "Fredoka, sans-serif" }}>{t.parent_name}</span>
                    <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: style.bg, color: style.fg }}>{style.label}</span>
                    {t.disposition && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#E3EFFC] text-[#1C3D6E]">{t.disposition}</span>}
                  </div>
                  <span className="text-sm font-semibold text-forest/60 flex items-center gap-1.5"><CalendarClock className="w-4 h-4 text-tangerine" /> {fmtDate(t.preferred_date, t.time_slot)}</span>
                </div>
                <p className="text-sm text-forest/50 mt-1">{t.phone} · {t.email}</p>
                {lastNote && <p className="text-xs text-forest/60 mt-2 italic">Last note ({fmtNoteTime(lastNote.created_at)}): "{lastNote.text}"</p>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Follow-ups view ---------------- */
function FollowUpsView({ tours, onSelect, onMarkDone }) {
  const due = tours.filter(needsFollowUp).sort((a, b) => (tourDateTime(a) - tourDateTime(b)));
  return (
    <div data-testid="admin-followups-view">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-[#FDE7E7] flex items-center justify-center"><Bell className="w-6 h-6" style={{ color: "#D7352B" }} /></div>
        <div>
          <h3 className="text-xl font-bold text-forest" style={{ fontFamily: "Fredoka, sans-serif" }}>Follow-ups due</h3>
          <p className="text-sm text-forest/50">Families whose tour was 24+ hours ago and haven't been followed up yet.</p>
        </div>
      </div>
      {due.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center border border-black/5" data-testid="admin-followups-empty">
          <CheckCircle2 className="w-12 h-12 mx-auto text-[#3D9B4F]/40 mb-3" />
          <p className="text-forest/60 font-semibold">All caught up — no follow-ups due right now.</p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="admin-followups-list">
          {due.map((t) => {
            const dt = tourDateTime(t);
            const hrs = Math.floor((Date.now() - dt.getTime()) / 3600000);
            const ago = hrs >= 48 ? `${Math.floor(hrs / 24)} days ago` : `${hrs} hours ago`;
            return (
              <div key={t.id} data-testid={`admin-followup-card-${t.id}`} className="bg-white rounded-2xl p-4 border-l-4 border-[#D7352B] shadow-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <button onClick={() => onSelect(t)} className="text-lg font-bold text-forest hover:text-[#D7352B] transition text-left" style={{ fontFamily: "Fredoka, sans-serif" }} data-testid={`admin-followup-name-${t.id}`}>{t.parent_name}</button>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#FDE7E7] text-[#D7352B]">Toured {ago}</span>
                </div>
                <p className="text-sm text-forest/50 mt-1">Tour was {fmtDate(t.preferred_date, t.time_slot)}{t.child_name ? ` · ${t.child_name}` : ""}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm">
                  <a href={`tel:${t.phone}`} className="flex items-center gap-1.5 text-blue-600 font-semibold hover:underline"><Phone className="w-4 h-4" /> {t.phone}</a>
                  <a href={`mailto:${t.email}`} className="flex items-center gap-1.5 text-blue-600 font-semibold hover:underline"><Mail className="w-4 h-4" /> {t.email}</a>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button onClick={() => onSelect(t)} data-testid={`admin-followup-open-${t.id}`} className="rounded-full px-4 py-2 text-sm font-semibold bg-forest text-cream hover:brightness-110 transition">Open &amp; add note</button>
                  <button onClick={() => onMarkDone(t.id)} data-testid={`admin-followup-done-${t.id}`} className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-[#E6F4EA] text-[#3D9B4F] hover:brightness-95 transition"><CheckCircle2 className="w-4 h-4" /> Mark followed up</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ---------------- Add call-in appointment ---------------- */
function AddAppointmentModal({ token, onClose, onCreated }) {
  const [f, setF] = useState({
    parent_name: "", phone: "", email: "", child_name: "", child_age: "",
    preferred_date: "", time_slot: "", source: "Call-in", message: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErr(""); };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.parent_name.trim() || !f.phone.trim()) { setErr("Parent name and phone are required."); return; }
    setSaving(true); setErr("");
    try {
      const payload = {};
      Object.entries(f).forEach(([k, v]) => { if (String(v).trim() !== "") payload[k] = v; });
      const { data } = await axios.post(`${API}/admin/tours`, payload, { headers: { Authorization: `Bearer ${token}` } });
      onCreated(data.request, data.email_sent);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not save appointment — please check the details and try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-add-appointment-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-cream h-full flex flex-col shadow-2xl">
        <div className="flex-shrink-0 bg-forest text-cream px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Fredoka, sans-serif" }}><PlusCircle className="w-5 h-5" /> New Call-in Appointment</h2>
          <button onClick={onClose} data-testid="admin-add-close" className="p-1.5 rounded-full hover:bg-white/15"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3 pb-12">
          <p className="text-sm text-forest/60 -mt-1 mb-1">Booked over the phone? Enter it here — any date and time is allowed.</p>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Parent name *</label>
            <input value={f.parent_name} onChange={set("parent_name")} className={inputCls} data-testid="add-parent-name" placeholder="Jane Doe" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Phone *</label>
              <input value={f.phone} onChange={set("phone")} className={inputCls} data-testid="add-phone" placeholder="(321) 555-0100" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Child's age</label>
              <input value={f.child_age} onChange={set("child_age")} className={inputCls} data-testid="add-child-age" placeholder="e.g. 3" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Email <span className="text-forest/30 normal-case font-semibold">(sends confirmation)</span></label>
            <input type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="add-email" placeholder="parent@example.com" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Child's name</label>
            <input value={f.child_name} onChange={set("child_name")} className={inputCls} data-testid="add-child-name" placeholder="Optional" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Tour date</label>
              <input type="date" value={f.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="add-date" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Tour time</label>
              <input type="time" value={f.time_slot} onChange={set("time_slot")} className={inputCls} data-testid="add-time" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">How did you hear about us?</label>
            <select value={f.source} onChange={set("source")} className={inputCls} data-testid="add-source">
              {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-forest/50">Notes</label>
            <textarea rows={2} value={f.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="add-message" placeholder="Anything they mentioned on the call…" />
          </div>

          {err && <p className="text-sm font-semibold text-center" style={{ color: "#D7352B" }} data-testid="admin-add-error">{err}</p>}
          <button type="submit" disabled={saving} data-testid="admin-add-submit" className="w-full rounded-full bg-[#D7352B] text-white font-bold py-3.5 flex items-center justify-center gap-2 hover:brightness-110 transition disabled:opacity-60">
            <PlusCircle className="w-5 h-5" /> {saving ? "Saving…" : "Save appointment"}
          </button>
        </form>
      </div>
    </div>
  );
}


/* ---------------- Dashboard ---------------- */
function Dashboard({ token, onLogout }) {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setViewState] = useState(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return ["list", "calendar", "search", "marketing", "followups"].includes(stored) ? stored : "list";
  });
  const setView = useCallback((v) => { setViewState(v); localStorage.setItem(VIEW_KEY, v); }, []);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/tours`, { headers: { Authorization: `Bearer ${token}` } });
      setTours(data);
    } catch (err) {
      if (err.response?.status === 401) { onLogout(); return; }
      setError("Could not load tour requests.");
    } finally { setLoading(false); }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/tours/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setTours((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    } catch (e) { setError("Could not update status."); }
  };

  const onSaved = (updated) => {
    setTours((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    setSelected(updated);
  };

  const onCreated = (created) => {
    setTours((prev) => [created, ...prev]);
    setShowAdd(false);
    setSelected(created);
  };

  const markFollowedUp = async (id) => {
    try {
      const { data } = await axios.patch(`${API}/admin/tours/${id}`, { followed_up: true }, { headers: { Authorization: `Bearer ${token}` } });
      setTours((prev) => prev.map((t) => (t.id === id ? data.request : t)));
    } catch (e) { setError("Could not update follow-up."); }
  };

  const followUpCount = tours.filter(needsFollowUp).length;

  const counts = {
    all: tours.length,
    new: tours.filter((t) => (t.status || "new") === "new").length,
    contacted: tours.filter((t) => t.status === "contacted").length,
    archived: tours.filter((t) => t.status === "archived").length,
  };

  const VIEWS = [
    { key: "list", label: "List", icon: ListIcon },
    { key: "calendar", label: "Calendar", icon: CalendarDays },
    { key: "search", label: "Search", icon: SearchIcon },
    { key: "marketing", label: "Marketing", icon: BarChart3 },
    { key: "followups", label: "Follow-ups", icon: Bell, badge: followUpCount },
  ];

  return (
    <div className="min-h-screen bg-cream grain overflow-x-hidden" data-testid="admin-dashboard">
      <header className="bg-forest text-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-amber">The Learning Tree Academy</p>
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Fredoka, sans-serif" }}>Tour Requests</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 transition px-4 py-2 text-sm font-semibold"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 transition px-4 py-2 text-sm font-semibold"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
          <div className="flex items-center gap-2 flex-wrap" data-testid="admin-view-switcher">
            {VIEWS.map((v) => (
              <button key={v.key} onClick={() => setView(v.key)} data-testid={`admin-view-${v.key}`}
                className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-full border-2 transition ${view === v.key ? "bg-amber text-forest border-amber shadow-lg" : "bg-white/5 text-cream border-cream/30 hover:bg-white/15"}`}>
                <v.icon className="w-4 h-4" /> {v.label}
                {v.badge > 0 && (
                  <span data-testid="admin-followup-badge" className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#D7352B] text-white text-xs font-extrabold flex items-center justify-center animate-pulse">{v.badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <GreetingBanner />
        {error && <p className="mb-4 text-sm font-semibold" style={{ color: "#D7352B" }} data-testid="admin-error">{error}</p>}
        {loading ? <p className="text-forest/50" data-testid="admin-loading">Loading…</p> : (
          <>
            {view === "list" && <ListView tours={tours} filter={filter} setFilter={setFilter} counts={counts} onSelect={setSelected} onStatus={updateStatus} onAdd={() => setShowAdd(true)} />}
            {view === "calendar" && <CalendarView tours={tours} onSelect={setSelected} onAdd={() => setShowAdd(true)} />}
            {view === "search" && <SearchView tours={tours} onSelect={setSelected} />}
            {view === "marketing" && <MarketingView tours={tours} />}
            {view === "followups" && <FollowUpsView tours={tours} onSelect={setSelected} onMarkDone={markFollowedUp} />}
          </>
        )}
      </main>

      {selected && <TourDetail key={selected.id} tour={selected} token={token} onClose={() => setSelected(null)} onSaved={onSaved} />}
      {showAdd && <AddAppointmentModal token={token} onClose={() => setShowAdd(false)} onCreated={onCreated} />}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const logout = useCallback(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }, []);
  if (!token) return <LoginForm onSuccess={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
